import { Link, Stack, Typography } from "@mui/material";
import parse from "html-react-parser";
import { useEffect, useState } from "react";
import ReactDOMServer from "react-dom/server";
import TagManager from "react-gtm-module";
import { useLocation } from "react-router-dom";
import { useApiContext } from "../api/ApiProvider";
import { StaticTimeTable } from "../components/TimeTable/StaticTimeTable";
import type { GetAvailabilitiesResponse } from "../shared/web/schema/getAvailabilities";
import type { Course, Store } from "../shared/web/schema/getStores";
import useAsyncUpdate from "../util/useAsyncUpdate";

export default function LpPage() {
  const { api } = useApiContext();
  const location = useLocation();
  const [group] = useAsyncUpdate(api.GetGroup);
  const [selecting, setSelecting] = useState<{ store: Store; course: Course } | null>();
  const [availabilityWithPage, setAvailabilityWithPage] = useState<GetAvailabilitiesResponse | null>();
  const [pageContent, setPageContent] = useState<string | null>();
  const [bottomFloatOpacity, setBottomFloatOpacity] = useState(0);
  const queryParams = new URLSearchParams(window.location.search);
  const storeId = queryParams.get("shop");
  const courseId = queryParams.get("course");
  const pageBaseUrl = `/static/lp/group_${group?.id}/shop_${storeId}/course_${courseId}/`;

  useEffect(() => {
    if (!group) return;
    api.GetStores(group.id, false).then((response) => {
      if (response === null) return;
      const store = response.stores?.at(0);
      const course = store?.courses?.find((course) => course.id.toString() === courseId);
      if (!store || !course) {
        setSelecting(null);
        return;
      }
      setSelecting({ course, store });
      api.GetNearestAvailability({ course_id: course.id, store_id: store.id }).then(setAvailabilityWithPage);

      if (store.google_tag_manager_id !== "") {
        TagManager.initialize({ gtmId: store.google_tag_manager_id });
      }
    });
  }, [group]);

  useEffect(() => {
    if (!selecting || !availabilityWithPage || !group) {
      return;
    }

    fetch(`${pageBaseUrl}index.html`)
      .then((response) => response.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        document.title = doc.title;

        // ページのベースURLを変更して画像等のリソースが正常に読み込めるようにする
        const baseElement = doc.createElement("base");
        baseElement.href = pageBaseUrl;
        doc.head.prepend(baseElement);

        // apple環境で<img>タグを読み込む場合、<base>タグを使ったベースURL指定がうまく動かないためsrcごと書き換える
        const imgTags = doc.querySelectorAll("img");
        imgTags.forEach((img) => {
          const currentSrc = img.getAttribute("src");
          if (currentSrc && !currentSrc.startsWith("http://") && !currentSrc.startsWith("https://")) {
            const newSrc = pageBaseUrl + currentSrc.replace(/^\.\//, "");
            img.setAttribute("src", newSrc);
          }
        });

        // カレンダー要素を探して置き換えを行う
        const calenderElement = doc.querySelector("reservation-calender");
        if (calenderElement) {
          calenderElement.outerHTML = ReactDOMServer.renderToString(
            <Stack style={{ alignItems: "center", lineHeight: 1.5, padding: "8px", width: "100%" }}>
              <Stack style={{ width: "100%" }}>
                <Stack style={{ backgroundColor: "#a87d52", padding: "16px", width: "100%" }}>
                  <Typography style={{ color: "white", fontFamily: "ＭＳ Ｐ明朝", fontWeight: "bold", margin: "0px" }}>{selecting.store.name}</Typography>
                </Stack>
                <StaticTimeTable availability={availabilityWithPage} cellLink={`/reserve/${location.search}`} rowCount={6} />
              </Stack>
            </Stack>,
          );
        }

        // ボタン要素を探して置き換えを行う
        const reservationButtonElements = doc.querySelectorAll("reservation-button");
        reservationButtonElements.forEach((reservationButtonElement) => {
          // スタイルと子要素を引き継ぐ
          const reservationButtonStyle = getStyleFromElement(reservationButtonElement);
          reservationButtonElement.outerHTML = ReactDOMServer.renderToString(
            <Link href={`/reserve/${location.search}`} style={reservationButtonStyle}>
              {parse(reservationButtonElement.innerHTML)}
            </Link>,
          );
        });

        setPageContent(doc.documentElement.outerHTML);
      })
      .catch((error) => {
        console.error("ページ読み込み中にエラーが発生しました: ", error);
        setPageContent(null);
      });
  }, [availabilityWithPage]);

  useEffect(() => {
    document.title = `コース説明 -${group?.domain}-`;

    const handleScroll = () => {
      const opacity = document.documentElement.scrollTop > 100 ? 1 : 0;
      setBottomFloatOpacity(opacity);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  if (pageContent === null || group === null || selecting === null) {
    return <Typography>ページのロードに失敗しました。</Typography>;
  }

  if (pageContent === undefined || group === undefined || selecting === undefined) {
    return <Typography>ページをロード中です...</Typography>;
  }

  return (
    <Stack>
      <Stack alignItems="center" bgcolor="white" boxShadow="0 0 5px gray" justifyContent="center" left={0} padding="4px 8px" position="sticky" top={0} width="100%" zIndex={1}>
        <Stack alignItems="center" direction="row" justifyContent="space-between" maxWidth="800px" width="100%">
          <img alt="logo" src={`${pageBaseUrl}resource/images/float/Header-Logo.png`} style={{ maxHeight: "50px", maxWidth: "100%" }} />
          <Stack direction="row" maxHeight="50px" maxWidth="100%">
            <Link alignItems="center" display="flex" href={`tel:${selecting.store.phone}`}>
              <img alt="phone" src={`${pageBaseUrl}resource/images/button/Reservation_Button_Phone.png`} style={{ maxHeight: "50px", maxWidth: "100%" }} />
            </Link>
            <Link alignItems="center" display="flex" href={`/reserve/${location.search}`}>
              <img alt="web" src={`${pageBaseUrl}resource/images/button/Reservation_Button_Web.png`} style={{ maxHeight: "50px", maxWidth: "100%" }} />
            </Link>
          </Stack>
        </Stack>
      </Stack>
      <div dangerouslySetInnerHTML={{ __html: pageContent }} />
      <Stack alignItems="center" bgcolor="#634070" bottom={0} justifyContent="center" padding="4px 8px" position="sticky" sx={{ opacity: bottomFloatOpacity, transition: "opacity 0.5s ease-in-out" }} width="100%">
        <Stack alignItems="center" direction="row" justifyContent="space-between" maxWidth="800px" width="100%">
          <Typography color="white" fontFamily="ＭＳ Ｐ明朝">
            ご予約、空き枠検索はこちらから
          </Typography>
          <Link height="50px" href={`/reserve/${location.search}`}>
            <img alt="reserve" height="50px" src={`${pageBaseUrl}resource/images/button/Reservation_Button_Web.png`} />
          </Link>
        </Stack>
      </Stack>
    </Stack>
  );
}

function getStyleFromElement(element: Element | null) {
  const styleString = element?.getAttribute("style");

  return styleString
    ? styleString.split(";").reduce((acc: { [key: string]: string }, style) => {
        const [key, value] = style.split(":");
        if (key && value) {
          acc[key.trim()] = value.trim();
        }
        return acc;
      }, {})
    : {};
}
